import { useEffect, useState } from 'react';
import {
  Box, CircularProgress, Dialog, DialogContent, DialogTitle, TextField, Tooltip, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { T, Icon, stitchCard, stitchInputSx, stitchSectionLabel, stitchTooltipSlotProps } from '../ProjectOverview/parts/stitch';
import { TEKST, stilleLinkSx } from './rustigeKop';

/**
 * Het klantpaspoort: tone-of-voice, kernboodschap, do's & don'ts en huisstijl
 * van de klant van dít project — zichtbaar naast de flow, zodat een script of
 * concept nooit "los van de klant" gemaakt wordt.
 *
 * De brug: project.fields['SourceRecordID Account (from Klantnaam)'] geeft het
 * Accounts-record-id waar AccountProfile en HuisstijlElement aan hangen.
 * Reclamebureau-projecten (opdrachtgever ≠ eindklant, bv. Brandwebbing →
 * Previder) kunnen per project een afwijkend account kiezen; die override
 * leeft op de flow (passport_account_airtable_id) en wint van de keten.
 */
// "DO: … DONT: …"-tekst opknippen in losse regels met ✓/✗ — leest als
// afspraken i.p.v. een lap tekst. Tekst zonder DO/DONT-markers blijft gewoon staan.
const parseDoDont = (tekst) => {
  const delen = String(tekst || '').split(/(?=\bDO(?:N'?T)?\s*:)/i).map((d) => d.trim()).filter(Boolean);
  const regels = delen.map((d) => {
    const m = d.match(/^DO(N'?T)?\s*:\s*(.*)$/is);
    if (!m) return null;
    return { dont: !!m[1], tekst: m[2].trim() };
  });
  return regels.every(Boolean) && regels.length > 0 ? regels : null;
};

const PROFILE_VELDEN = [
  { key: 'tone_of_voice', label: 'Tone of voice' },
  { key: 'kernboodschap', label: 'Kernboodschap' },
  { key: 'doelgroep', label: 'Doelgroep' },
  { key: 'usp', label: 'USP' },
  { key: 'do_dont', label: "Do's & don'ts" },
];

function ClientPassportCard({ project, clientName, flow, onFlowChanged, onCompactChange }) {
  const navigate = useNavigate();
  const projectRecId = project?.airtable_id || project?.id;
  const raw = project?.fields?.['SourceRecordID Account (from Klantnaam)'];
  const ketenAccountId = Array.isArray(raw) ? raw[0] : raw || null;
  // Override (eindklant) wint van de normale bedrijfsketen.
  const overrideId = flow?.passport_account_airtable_id || null;
  const accountId = overrideId || ketenAccountId;

  const [profile, setProfile] = useState(null);
  const [huisstijl, setHuisstijl] = useState([]);
  const [overrideNaam, setOverrideNaam] = useState('');
  const [loading, setLoading] = useState(false);
  const [kiesOpen, setKiesOpen] = useState(false);
  const [zoek, setZoek] = useState('');
  const [zoekResultaten, setZoekResultaten] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!accountId) { setProfile(null); setHuisstijl([]); return; }
    let actueel = true;
    (async () => {
      setLoading(true);
      try {
        const [pr, hr] = await Promise.all([
          authFetch(`${BACKEND_URL}/Api/klanten/${accountId}/profile`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
          authFetch(`${BACKEND_URL}/Api/klanten/${accountId}/huisstijl`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
        ]);
        if (!actueel) return;
        setProfile(pr?.profile || null);
        setHuisstijl(hr?.elements || hr || []);
      } finally { if (actueel) setLoading(false); }
    })();
    return () => { actueel = false; };
  }, [accountId]);

  // Naam van het gekozen eindklant-account (alleen bij een override nodig).
  useEffect(() => {
    if (!overrideId) { setOverrideNaam(''); return; }
    let actueel = true;
    authFetch(`${BACKEND_URL}/Api/klanten/${overrideId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (actueel) setOverrideNaam(d?.account?.fields?.Account || ''); })
      .catch(() => { /* naam is cosmetisch */ });
    return () => { actueel = false; };
  }, [overrideId]);

  // Zoeken met kleine debounce — hergebruikt /Api/klanten?search=.
  useEffect(() => {
    if (!kiesOpen) return undefined;
    const term = zoek.trim();
    if (term.length < 2) { setZoekResultaten(null); return undefined; }
    const timer = setTimeout(async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/klanten?search=${encodeURIComponent(term)}`);
        const d = r.ok ? await r.json() : null;
        setZoekResultaten(d?.klanten || []);
      } catch { setZoekResultaten([]); }
    }, 350);
    return () => clearTimeout(timer);
  }, [zoek, kiesOpen]);

  const zetOverride = async (nieuwId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passport_account_airtable_id: nieuwId || '' }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      setKiesOpen(false);
      setZoek('');
      setZoekResultaten(null);
      toast.success(nieuwId ? 'Eindklant gekoppeld — paspoort volgt nu dit account' : 'Terug naar de standaard klantkoppeling');
      onFlowChanged?.();
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); }
    finally { setBusy(false); }
  };

  const logo = Array.isArray(huisstijl) ? huisstijl.find((e) => (e.type || '').toLowerCase() === 'logo') : null;
  const fonts = Array.isArray(huisstijl) ? huisstijl.filter((e) => (e.type || '').toLowerCase() === 'font') : [];
  const gevuld = PROFILE_VELDEN.filter((v) => profile?.[v.key]);
  // Zonder profiel (of zonder account) is het paspoort één stille regel i.p.v.
  // een losse kaart (Joris, 25 sep: rustiger). Tijdens laden ook compact.
  const compact = loading || !accountId
    || (gevuld.length === 0 && !(logo?.preview_url || logo?.file_url) && fonts.length === 0);
  useEffect(() => { onCompactChange?.(compact); }, [compact, onCompactChange]);

  const kiesDialog = (
        <Dialog open={kiesOpen} onClose={() => !busy && setKiesOpen(false)} maxWidth="xs" fullWidth
          onClick={(e) => e.stopPropagation()}>
          <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>Eindklant kiezen</DialogTitle>
          <DialogContent>
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 1.5 }}>
              Het paspoort (tone-of-voice, huisstijl, do's &amp; don'ts) volgt dan dit account
              in plaats van de klant op het project. Alleen voor dit project.
            </Typography>
            <TextField autoFocus fullWidth size="small" placeholder="Zoek een account…"
              value={zoek} onChange={(e) => setZoek(e.target.value)} sx={stitchInputSx} />
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 300, overflowY: 'auto' }}>
              {zoek.trim().length < 2 ? (
                <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant }}>
                  Typ minimaal 2 tekens
                </Typography>
              ) : zoekResultaten === null ? (
                <CircularProgress size={16} sx={{ my: 1, mx: 'auto' }} />
              ) : zoekResultaten.length === 0 ? (
                <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant }}>
                  Geen accounts gevonden
                </Typography>
              ) : zoekResultaten.map((k) => (
                <Box key={k.id} onClick={() => !busy && zetOverride(k.id)} sx={{
                  px: 1.25, py: 0.9, borderRadius: '0.5rem', cursor: 'pointer',
                  fontFamily: T.font, fontSize: '0.85rem', fontWeight: 700, color: T.text,
                  '&:hover': { bgcolor: T.surfaceLow },
                }}>
                  {k.fields?.Account || k.id}
                  {(k.fields?.BedrijvenNamen || []).length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', fontFamily: T.fontBody, color: T.outlineVariant }}>
                      {(k.fields.BedrijvenNamen || []).slice(0, 3).join(', ')}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </DialogContent>
        </Dialog>
  );

  if (compact) {
    const naam = (overrideId ? overrideNaam || clientName : clientName);
    const deel = (inhoud, key) => (
      <Box key={key} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" aria-hidden sx={{ color: T.outlineVariant }}>·</Box>
        {inhoud}
      </Box>
    );
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
        fontFamily: T.fontBody, fontSize: TEKST, color: T.outline }}>
        <Box component="span" sx={{ color: T.text, fontWeight: 600 }}>{naam || 'Klantpaspoort'}</Box>
        {overrideId && deel(`eindklant (afwijkend van ${clientName || 'projectklant'})`, 'eind')}
        {deel(loading ? 'paspoort ophalen…' : !accountId ? (
          <Tooltip title={'Koppel het bedrijf in Airtable (veld "Account" op Bedrijven) om het paspoort te vullen'}
            slotProps={stitchTooltipSlotProps}>
            <Box component="span">nog niet aan een account gekoppeld</Box>
          </Tooltip>
        ) : (
          <Tooltip title="Missie, tone-of-voice, do's & don'ts — invullen kan in de Account Workspace"
            slotProps={stitchTooltipSlotProps}>
            <Box component="span">nog geen klantprofiel</Box>
          </Tooltip>
        ), 'status')}
        {accountId && !loading && deel(
          <Box component="button" type="button" sx={stilleLinkSx}
            onClick={() => navigate(`/accounts/${accountId}/workspace`)}>profiel invullen</Box>, 'account')}
        {deel(overrideId ? (
          <Box component="button" type="button" sx={stilleLinkSx} onClick={() => zetOverride(null)}>herstellen</Box>
        ) : (
          <Tooltip title="Voor bureau-projecten: kies het account van de eindklant (bv. Previder bij een Brandwebbing-opdracht)"
            slotProps={stitchTooltipSlotProps}>
            <Box component="button" type="button" sx={stilleLinkSx} onClick={() => setKiesOpen(true)}>andere eindklant</Box>
          </Tooltip>
        ), 'kies')}
        {kiesDialog}
      </Box>
    );
  }

  return (
    <Box
      onClick={accountId ? () => navigate(`/accounts/${accountId}/workspace`) : undefined}
      sx={{ ...stitchCard, p: 2, cursor: accountId ? 'pointer' : 'default',
        '&:hover': { boxShadow: T.shadow, transform: 'none' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon name="badge" size={18} sx={{ color: T.tertiary }} />
        <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '0.9rem', color: T.text, flex: 1 }}>
          Klantpaspoort
        </Typography>
        {accountId && (
          <Typography variant="caption" onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${accountId}/workspace`); }}
            sx={{ fontFamily: T.fontBody, color: T.primary, cursor: 'pointer', fontWeight: 700,
              '&:hover': { textDecoration: 'underline' } }}>
            account →
          </Typography>
        )}
      </Box>

      {(overrideId ? overrideNaam || clientName : clientName) && (
        <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.95rem', color: T.text, mb: 0.25 }}>
          {overrideId ? (overrideNaam || clientName) : clientName}
        </Typography>
      )}

      {/* Eindklant-override: kiezen / tonen / herstellen — voor bureau-projecten */}
      <Box sx={{ mb: 1 }} onClick={(e) => e.stopPropagation()}>
        {overrideId ? (
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant }}>
            eindklant (afwijkend van {clientName || 'projectklant'}) ·{' '}
            <Box component="span" onClick={() => zetOverride(null)}
              sx={{ color: T.primary, cursor: 'pointer', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>
              herstellen
            </Box>
          </Typography>
        ) : (
          <Tooltip title="Voor bureau-projecten: kies het account van de eindklant (bv. Previder bij een Brandwebbing-opdracht)"
            slotProps={stitchTooltipSlotProps}>
            <Typography variant="caption" onClick={() => setKiesOpen(true)}
              sx={{ fontFamily: T.fontBody, color: T.outlineVariant, cursor: 'pointer',
                '&:hover': { color: T.primary } }}>
              andere eindklant kiezen
            </Typography>
          </Tooltip>
        )}
      </Box>

      {!accountId ? (
        <Tooltip title={'Koppel het bedrijf in Airtable (veld "Account" op Bedrijven) om het paspoort te vullen'}
          slotProps={stitchTooltipSlotProps}>
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, cursor: 'default' }}>
            Nog niet aan een account gekoppeld
          </Typography>
        </Tooltip>
      ) : loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: T.outline }}>
          <CircularProgress size={14} />
          <Typography variant="caption" sx={{ fontFamily: T.fontBody }}>Paspoort ophalen…</Typography>
        </Box>
      ) : (
        <>
          {logo?.preview_url || logo?.file_url ? (
            <Box sx={{ mb: 1.5, p: 1, borderRadius: '0.5rem', bgcolor: T.surfaceLow, display: 'grid', placeItems: 'center' }}>
              <Box component="img" src={logo.preview_url || logo.file_url} alt="logo"
                sx={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain' }} />
            </Box>
          ) : null}

          {gevuld.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {gevuld.map((v) => {
                const doDont = v.key === 'do_dont' ? parseDoDont(profile[v.key]) : null;
                return (
                  <Box key={v.key}>
                    <Typography sx={{ ...stitchSectionLabel, fontSize: '0.6rem' }}>{v.label}</Typography>
                    {doDont ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
                        {doDont.map((r, i) => (
                          <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                            <Icon name={r.dont ? 'cancel' : 'check_circle'} size={14}
                              sx={{ color: r.dont ? '#dc2626' : '#059669', mt: '2px', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.text, lineHeight: 1.5 }}>
                              {r.tekst}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.text, whiteSpace: 'pre-wrap',
                        display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {profile[v.key]}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Tooltip title="Missie, tone-of-voice, do's & don'ts — invullen kan in de Account Workspace (klik op account →)"
              slotProps={stitchTooltipSlotProps}>
              <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, cursor: 'default' }}>
                Nog geen klantprofiel
              </Typography>
            </Tooltip>
          )}

          {fonts.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ ...stitchSectionLabel, fontSize: '0.6rem' }}>Fonts</Typography>
              <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.text }}>
                {fonts.map((f) => f.name || f.label).filter(Boolean).join(', ') || `${fonts.length} font(s)`}
              </Typography>
            </Box>
          )}
        </>
      )}

      {kiesDialog}
    </Box>
  );
}

export default ClientPassportCard;
